import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
  Badge,
} from "@mui/material";
import { MusicNote } from "@mui/icons-material";
import {
  collection,
  onSnapshot,
  orderBy,
  query,
  limit,
} from "firebase/firestore";
import { database } from "../firebase/config";
import { useParams } from "react-router-dom";
import { getEventSongsData } from "../data/songsData";

const RealTimeMonitorPage = () => {
  const { eventKey } = useParams();
  const eventId = eventKey || "sanremo";
  const { songs } = getEventSongsData(eventId);
  const [recentVotes, setRecentVotes] = useState([]);
  const [totalVotes, setTotalVotes] = useState(0);
  const [activeUsers, setActiveUsers] = useState(new Set());
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    // Query per ascoltare i voti in tempo reale
    const votesCollection = collection(database, "votes");
    const votesQuery = query(
      votesCollection,
      orderBy("timestamp", "desc"),
      limit(200),
    );

    // Listener in tempo reale
    const unsubscribe = onSnapshot(
      votesQuery,
      (snapshot) => {
        setIsConnected(true);
        const votes = [];
        const users = new Set();

        snapshot.forEach((doc) => {
          const data = doc.data();
          const voteEventId =
            data.eventId === undefined || data.eventId === null
              ? "sanremo"
              : data.eventId;
          if (voteEventId !== eventId) return;
          const jsTimestamp = data.timestamp?.toDate
            ? data.timestamp.toDate()
            : new Date(data.timestamp);

          votes.push({
            id: doc.id,
            ...data,
            timestamp: jsTimestamp,
          });

          users.add(
            data.userIdentifier || data.userEmail || "Utente sconosciuto",
          );
        });

        setRecentVotes(votes);
        setTotalVotes(votes.length);
        setActiveUsers(users);
        setLastUpdate(new Date());

        console.log("Nuovo voto ricevuto in tempo reale:", votes[0]);
      },
      (error) => {
        console.error("Errore nel listener Firebase:", error);
        setIsConnected(false);
      },
    );

    // Cleanup del listener quando il componente viene smontato
    return () => unsubscribe();
  }, [eventId]);

  const getSongInfo = (songKey) => {
    return (
      songs.find((song) => song.key === songKey) || {
        artist: "Sconosciuto",
        title: "Titolo sconosciuto",
        country: "Paese sconosciuto",
        color: "#cccccc",
      }
    );
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const diff = now - timestamp;
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);

    if (seconds < 60) return `${seconds}s fa`;
    if (minutes < 60) return `${minutes}m fa`;
    if (hours < 24) return `${hours}h fa`;
    return timestamp.toLocaleDateString();
  };

  const getVoteDetails = (votes) => {
    const categories = {
      canzone: "Canzone",
      presenza_scenica: "Presenza",
      esibizione: "Esibizione",
    };

    return Object.entries(votes).map(([category, score]) => ({
      category: categories[category] || category,
      score,
    }));
  };

  const getAverageScore = (votes) => {
    const scores = Object.values(votes);
    return scores.length > 0
      ? (scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(1)
      : "0";
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: "auto" }}>
      {/* Header */}
      <Paper
        elevation={3}
        sx={{
          p: 3,
          mb: 3,
          background: "linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)",
          color: "white",
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h4" gutterBottom>
              🔴 Monitor Tempo Reale
            </Typography>
            <Typography variant="body1">
              Monitoraggio live di tutti i salvataggi nel database
            </Typography>
          </Box>
          <Box textAlign="right">
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <Badge color={isConnected ? "success" : "error"} variant="dot">
                <Typography variant="body2">
                  {isConnected ? "Connesso" : "Disconnesso"}
                </Typography>
              </Badge>
            </Box>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              Ultimo aggiornamento: {lastUpdate.toLocaleTimeString()}
            </Typography>
          </Box>
        </Box>
      </Paper>

      <Grid container spacing={3}>
        {/* Statistiche */}
        <Grid item xs={12} md={4}>
          <Paper elevation={2} sx={{ p: 2, height: "fit-content" }}>
            <Typography variant="h6" gutterBottom color="primary">
              📊 Statistiche Live
            </Typography>
            <Box display="flex" flexDirection="column" gap={2}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="body2">Voti totali:</Typography>
                <Chip label={totalVotes} color="primary" size="small" />
              </Box>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="body2">Utenti attivi:</Typography>
                <Chip label={activeUsers.size} color="success" size="small" />
              </Box>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="body2">Ultimo voto:</Typography>
                <Chip
                  label={
                    recentVotes.length > 0
                      ? formatTimeAgo(recentVotes[0].timestamp)
                      : "Nessuno"
                  }
                  color="info"
                  size="small"
                />
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Lista Voti in Tempo Reale */}
        <Grid item xs={12} md={8}>
          <Paper
            elevation={2}
            sx={{ p: 2, maxHeight: "600px", overflow: "auto" }}
          >
            <Typography variant="h6" gutterBottom color="primary">
              🎵 Voti in Tempo Reale
            </Typography>
            <List>
              {recentVotes.map((vote, index) => {
                const songInfo = getSongInfo(vote.idSong);
                const voteDetails = getVoteDetails(vote.votes);
                const averageScore = getAverageScore(vote.votes);

                return (
                  <React.Fragment key={vote.id}>
                    <ListItem alignItems="flex-start">
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: songInfo.color }}>
                          <MusicNote />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box
                            display="flex"
                            alignItems="center"
                            gap={1}
                            flexWrap="wrap"
                          >
                            <Typography variant="subtitle1" component="span">
                              {vote.userIdentifier ||
                                vote.userEmail ||
                                "Utente sconosciuto"}
                            </Typography>
                            <Chip
                              label={`Media: ${averageScore}`}
                              size="small"
                              color="primary"
                            />
                            <Chip
                              label={formatTimeAgo(vote.timestamp)}
                              size="small"
                              variant="outlined"
                            />
                          </Box>
                        }
                        secondary={
                          <Box mt={1}>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              gutterBottom
                            >
                              <strong>{songInfo.artist}</strong> -{" "}
                              {songInfo.title} ({songInfo.country})
                            </Typography>
                            <Box display="flex" gap={1} flexWrap="wrap">
                              {voteDetails.map(({ category, score }) => (
                                <Chip
                                  key={category}
                                  label={`${category}: ${score}`}
                                  size="small"
                                  variant="outlined"
                                  color={
                                    score >= 8
                                      ? "success"
                                      : score >= 6
                                        ? "warning"
                                        : "default"
                                  }
                                />
                              ))}
                            </Box>
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < recentVotes.length - 1 && (
                      <Divider variant="inset" component="li" />
                    )}
                  </React.Fragment>
                );
              })}
              {recentVotes.length === 0 && (
                <ListItem>
                  <ListItemText
                    primary="Nessun voto ancora ricevuto"
                    secondary="I voti appariranno qui in tempo reale non appena vengono salvati nel database"
                  />
                </ListItem>
              )}
            </List>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default RealTimeMonitorPage;
